import { ChangeEvent } from 'react'
import isValidEmail from 'utils/isValidEmail'

interface Props {
  email: string
  hasError: boolean
  isSuccessful: boolean
  processing: boolean
  setEmail: (email: string) => void
  setIsSuccessful: (value: boolean) => void
  setHasError: (hasError: boolean) => void
  onSubmit: (email: string, hasError: boolean) => void
}

export function EmailInput({
  email,
  setEmail,
  hasError,
  setHasError,
  isSuccessful,
  setIsSuccessful,
  processing,
  onSubmit,
}: Props) {
  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    setIsSuccessful(false)
    setEmail(value)
  }

  const validate = () => {
    if (email.length === 0 || isValidEmail(email)) {
      setHasError(false)
    } else {
      setHasError(true)
    }
  }

  let buttonContent = null

  switch (true) {
    case !processing && !isSuccessful:
      // Nothing submitted yet
      buttonContent = 'Subscribe'
      break
    case !processing && !hasError && isSuccessful:
      // Submitted with success
      buttonContent = (
        <div className="display-flex justify-content-center align-items-center gap-2">
          <span className="Icon Icon--sm">
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" focusable="false">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M20.7203 6.65685L18.9283 4.86658L9.35544 14.2355L5.57678 10.5258L3.74977 12.3137L9.40662 17.9706L20.7203 6.65685Z"
                fill="currentColor"
              ></path>{' '}
              <title>Checkmark</title>
            </svg>
          </span>{' '}
          Subscribed!
        </div>
      )
      break
    case processing:
      // Processing the subscribe request
      buttonContent = <span>&nbsp;</span>
      break
    default:
      buttonContent = 'Subscribe'
      break
  }

  return (
    <div className="w-full display-flex justify-content-stretch max-w-xl flex-wrap md:flex-nowrap">
      <div className="w-full mr-0 md:mr-2">
        <div className="pos-relative">
          <input
            data-testid="rateForIt-input"
            value={email}
            onChange={onChangeHandler}
            onBlur={validate}
            className={`FormInput max-w-full ${hasError ? 'border-red' : ''}`}
            type="text"
            id="textInput"
          />
          {hasError && (
            <>
              <div className="pos-absolute mt-3 mr-3" style={{ top: 0, right: 0 }}>
                <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17.62 14.1431L9.74299 0.500156C9.65523 0.348145 9.529 0.221914 9.37698 0.134148C8.89869 -0.142001 8.2871 0.0218669 8.01095 0.500156L0.133981 14.1431C0.0462085 14.2951 0 14.4675 0 14.6431C0 15.1954 0.447715 15.6431 1 15.6431H16.7539C16.9295 15.6431 17.1019 15.5969 17.254 15.5091C17.7322 15.233 17.8961 14.6214 17.62 14.1431ZM16.7539 14.6431H1L8.87697 1.00017L16.7539 14.6431ZM9.87697 12.3931C9.87697 12.9454 9.42926 13.3931 8.87697 13.3931C8.32469 13.3931 7.87697 12.9454 7.87697 12.3931C7.87697 11.8408 8.32469 11.3931 8.87697 11.3931C9.42926 11.3931 9.87697 11.8408 9.87697 12.3931ZM9.85317 6.3719C9.85317 5.83132 9.41494 5.39309 8.87435 5.39309C8.84306 5.39309 8.81178 5.39457 8.78063 5.39754C8.23723 5.4493 7.83868 5.93177 7.89044 6.47517L8.20804 9.8094C8.23956 10.1403 8.51752 10.3931 8.84996 10.3931C9.18324 10.3931 9.46272 10.1414 9.49752 9.80996L9.84781 6.47412C9.85138 6.44016 9.85317 6.40604 9.85317 6.3719Z"
                    fill="#D71919"
                  />
                </svg>
              </div>
              <div className="text-left mt-2 type-body-two">Please enter a valid email address to continue.</div>
            </>
          )}
        </div>
      </div>
      <div className="w-full md:w-auto mt-4 md:mt-0">
        <button
          onClick={() => onSubmit(email, hasError)}
          className={`Button Button--primary size-full w-full md:w-48 h-12 ${processing ? 'is-loading' : ''}`}
        >
          {buttonContent}
        </button>
      </div>
    </div>
  )
}
